import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from './new-auth.service';

/** URL fragments that identify an endpoint of ours, which expects our bearer token. */
const AUTHENTICATED_URL_FRAGMENTS = ['execute-api', 'siemens', 'localhost:3000'];

/**
 * URL prefixes that must never receive our bearer token, even though they match one of the
 * fragments above. These are third-party or unauthenticated endpoints.
 */
const UNAUTHENTICATED_URL_PREFIXES = [
    'https://dcapis.siemens',
    environment.translationsUrl,
    environment.translationsUrlDev,
    environment.translationServiceUrl,
    environment.authentication.authority
];

@Injectable({
    providedIn: 'root'
})
export class TokenInjector implements HttpInterceptor {
    private readonly auth = inject(AuthService);

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (!this.shouldAttachToken(request.url)) {
            return next.handle(request);
        }

        return this.auth.getAccessToken().pipe(
            switchMap((token) => {
                if (token) {
                    request = request.clone({
                        setHeaders: {
                            Authorization: `Bearer ${token}`
                        }
                    });
                }
                return next.handle(request);
            })
        );
    }

    private shouldAttachToken(url: string): boolean {
        const isOwnEndpoint = AUTHENTICATED_URL_FRAGMENTS.some((fragment) => url.includes(fragment));
        const isExcluded = UNAUTHENTICATED_URL_PREFIXES.some((prefix) => url.startsWith(prefix));

        return isOwnEndpoint && !isExcluded;
    }
}
